import { Team } from 'src/API';
import { UpdateType } from 'src/api/project';
import { slice } from 'src/slices/team';
import type { AppThunk } from 'src/store';
import { teamApi, TeamInput } from '../api/team/index';
import { thunks as projectThunks } from './project';

type GetTeamUsersParams = {
  teamId: string;
};

type DeleteTeamParams = {
  teamId: string;
};

type AddUserToTeamParams = {
  teamId: string;
  email: string;
  role: string;
};

type RemoveUserFromTeamParams = {
  teamId: string;
  userId: string;
};

type RemoveInvitedUserParams = {
  invitedId: string;
};

type UpdateTeamPermissionsParams = {
  payload: any;
};

type GetManagementGroupsParams = {
  teamId: string;
};

type SetCurrentTeamParams = { team: Team };

const setTeam =
  (params: SetCurrentTeamParams): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(slice.actions.setTeam(params.team));
  };

const getTeams =
  (payload?: { teamId?: string }): AppThunk =>
  async (dispatch): Promise<void> => {
    console.log('🔄 THUNK: getTeams called with payload:', payload);
    dispatch(slice.actions.setPending({ attribute: 'listTeams' }));
    try {
      console.log('📡 THUNK: Calling teamApi.getTeams()');
      const response = await teamApi.getTeams();
      const teams = response?.data?.listTeams?.items;
      console.log('📦 THUNK: Teams received from API:', {
        teamsCount: teams?.length || 0,
        teams: teams?.map((t) => ({ id: t?.id, name: t?.name })) || [],
      });

      console.log('🏪 THUNK: Dispatching getTeams action with:', {
        teamId: payload?.teamId,
        teamsCount: teams?.length || 0,
      });
      dispatch(slice.actions.getTeams({ teamId: payload?.teamId, teams: (teams as Team[]) || [] }));

      dispatch(projectThunks.setInitialProjectFromTeams((teams as Team[]) || []));
      dispatch(
        slice.actions.setFulfilled({
          attribute: 'listTeams',
          message: 'Team fetched successfully',
        }),
      );
      console.log('✅ THUNK: getTeams completed successfully');
    } catch (error) {
      console.error('❌ THUNK: getTeams failed:', error);
      console.error('❌ THUNK: Error details:', {
        message: error?.message,
        data: error?.data,
        errors: error?.errors,
        graphQLErrors: error?.data?.errors,
        networkError: error?.networkError,
        status: error?.response?.status,
      });

      if (error?.response?.status === 401) {
        dispatch(slice.actions.setUnauthorized({ attribute: 'listTeams' }));
        return;
      }
      dispatch(
        slice.actions.setRejected({
          attribute: 'listTeams',
          message: error.message,
        }),
      );
    }
  };

const getBasicTeams =
  (payload?: { teamId?: string }): AppThunk =>
  async (dispatch): Promise<void> => {
    console.log('🔄 THUNK: getBasicTeams called with payload:', payload);
    dispatch(slice.actions.setPending({ attribute: 'listTeams' }));
    try {
      console.log('📡 THUNK: Calling teamApi.getBasicTeams() (without moduleConfigurations)');
      const response = await teamApi.getBasicTeams();
      const teams = response?.data?.listTeams?.items;
      console.log('📦 THUNK: Basic teams received from API:', {
        teamsCount: teams?.length || 0,
        teams: teams?.map((t) => ({ id: t?.id, name: t?.name })) || [],
      });

      console.log('🏪 THUNK: Dispatching getTeams action with basic data:', {
        teamId: payload?.teamId,
        teamsCount: teams?.length || 0,
      });
      dispatch(slice.actions.getTeams({ teamId: payload?.teamId, teams: (teams as Team[]) || [] }));

      dispatch(projectThunks.setInitialProjectFromTeams((teams as Team[]) || []));
      dispatch(
        slice.actions.setFulfilled({
          attribute: 'listTeams',
          message: 'Basic teams fetched successfully',
        }),
      );
      console.log('✅ THUNK: getBasicTeams completed successfully');
    } catch (error) {
      console.error('❌ THUNK: getBasicTeams failed:', error);
      console.error('❌ THUNK: Basic teams error details:', {
        message: error?.message,
        data: error?.data,
        errors: error?.errors,
        graphQLErrors: error?.data?.errors,
        networkError: error?.networkError,
        status: error?.response?.status,
      });

      if (error?.response?.status === 401) {
        dispatch(slice.actions.setUnauthorized({ attribute: 'listTeams' }));
        return;
      }
      dispatch(
        slice.actions.setRejected({
          attribute: 'listTeams',
          message: error.message,
        }),
      );
    }
  };

const updateTeam =
  (params: UpdateType<Team>): AppThunk =>
  async (dispatch): Promise<void> => {
    const response = await teamApi.updateTeam(params);
    dispatch(slice.actions.updateTeam(response));
  };
const getTeamUsers =
  (params: GetTeamUsersParams): AppThunk =>
  async (dispatch): Promise<void> => {
    const response = await teamApi.getTeamUsers(params.teamId);
    dispatch(slice.actions.getTeamUsers(response));
  };

const createTeam =
  (params: TeamInput): AppThunk =>
  async (dispatch): Promise<void> => {
    const response = await teamApi.create(params);
    dispatch(slice.actions.createTeam(response));
  };

const deleteTeam =
  (params: DeleteTeamParams): AppThunk =>
  async (dispatch): Promise<void> => {
    const response = await teamApi.delete(params.teamId);
    dispatch(slice.actions.deleteTeam(response));
  };

const addUserToTeam =
  (params: AddUserToTeamParams): AppThunk =>
  async (dispatch): Promise<void> => {
    const response = await teamApi.addUserToTeam(params.teamId, params.email, params.role);
    dispatch(slice.actions.addUserToTeam(response));
  };

const removeUserFromTeam =
  (params: RemoveUserFromTeamParams): AppThunk =>
  async (dispatch): Promise<void> => {
    const response = await teamApi.removeUserFromTeam(params.teamId, params.userId);
    dispatch(slice.actions.removeUserFromTeam(response));
  };

const removeInvitedUser =
  (params: RemoveInvitedUserParams): AppThunk =>
  async (dispatch): Promise<void> => {
    const response = await teamApi.removeInvitedUser(params.invitedId);
    dispatch(slice.actions.removeInvitedUser(response));
  };

const updateTeamPermissions =
  (params: UpdateTeamPermissionsParams): AppThunk =>
  async (dispatch): Promise<void> => {
    const response = await teamApi.updateTeamPermissions(params.payload);
    dispatch(slice.actions.updateTeamPermissions(response));
  };

const getManagementGroups =
  (params: GetManagementGroupsParams): AppThunk =>
  async (dispatch): Promise<void> => {
    const response = await teamApi.getManagementGroups(params.teamId);
    dispatch(slice.actions.getManagementGroups({ managementRoles: response?.result?.groups }));
    dispatch(
      slice.actions.setFulfilled({
        attribute: 'teamManagementGroups',
        message: 'Team management groups fetched successfully',
      }),
    );
  };

const addManagementGroupForTeam =
  (payload: { teamId: string; role: string }): AppThunk =>
  async (dispatch): Promise<void> => {
    try {
      const response = await teamApi.addManagementGroupForTeam(payload);
      if (response.status >= 200 && response.status < 300) {
        dispatch(thunks.getTeams());
      } else {
        dispatch(slice.actions.addManagementGroupForTeamFailure(response.data.errors));
      }
    } catch (error) {
      dispatch(slice.actions.addManagementGroupForTeamFailure(error.message));
    }
  };

const removeManagementForGroupTeam =
  (payload: { teamId: string; role: string }): AppThunk =>
  async (dispatch): Promise<void> => {
    try {
      const response = await teamApi.removeManagementForGroupTeam(payload);
      dispatch(slice.actions.removeManagementForGroupTeamSuccess(response.data));
      dispatch(thunks.getTeams());
    } catch (error) {
      dispatch(slice.actions.removeManagementForGroupTeamFailure(error.message));
    }
  };

const uploadTeamImage =
  (teamId: string, formData: FormData, onProgress: (progressEvent: ProgressEvent) => void): AppThunk =>
  async (dispatch): Promise<void> => {
    const response = await teamApi.uploadImage(teamId, formData, onProgress);
    dispatch(
      slice.actions.updateTeam({
        data: {
          updateTeam: response.result,
        } as any,
      }),
    );
  };

const removeTeamImage =
  (teamId: string): AppThunk =>
  async (dispatch): Promise<void> => {
    const response = await teamApi.removeImage(teamId);
    dispatch(
      slice.actions.updateTeam({
        data: {
          updateTeam: response.result,
        } as any,
      }),
    );
  };

export const thunks = {
  uploadTeamImage,
  removeTeamImage,
  setTeam,
  updateTeam,
  getTeams,
  getBasicTeams,
  getTeamUsers,
  createTeam,
  deleteTeam,
  addUserToTeam,
  removeUserFromTeam,
  removeInvitedUser,
  updateTeamPermissions,
  getManagementGroups,
  addManagementGroupForTeam,
  removeManagementForGroupTeam,
};
